import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Copy, Star } from "lucide-react";
import api, { errMsg } from "../../lib/api";
import SEO from "../../components/SEO";
import { TextF, AreaF, SelectF, CheckF, ImageF } from "../admin/AFields";
import { Modal } from "../admin/Participants";

const BLANK = { name: "", category: "Good Morning", message_en: "", message_hi: "", quote: "", quote_author: "", bg_image: "", is_default: false };
const CATS = ["Good Morning", "Festivals", "Birthdays", "Anniversaries", "National Days", "Awareness Days", "Inspirational", "Legal", "Custom"];

export default function StudioTemplates() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/greetings/templates").then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.post("/greetings/templates/save", editing); toast.success("Template saved"); setEditing(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const duplicate = async (t) => {
    try { await api.post(`/greetings/templates/${t.id}/duplicate`); toast.success("Template duplicated"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/greetings/templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="studio-templates">
      <SEO title="Greeting Templates" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl">Greeting Templates</h1>
          <p className="mt-1 text-sm text-slate-500">Reusable message templates in English and Hindi. Duplicate any template to adapt it for an occasion.</p>
        </div>
        <button onClick={() => setEditing({ ...BLANK })} className="btn-primary !rounded-md !px-5 !py-2.5 text-sm" data-testid="template-new-btn"><Plus size={15} /> New Template</button>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((t) => (
          <div key={t.id} className="rounded-lg border border-gray-200 bg-white p-5" data-testid={`template-row-${t.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate text-sm font-bold text-navy">
                  {t.name} {t.is_default && <span className="ml-1 rounded-full bg-saffron-100 px-2 py-0.5 text-[10px] font-bold text-saffron-600"><Star size={9} className="mr-0.5 inline" />DEFAULT</span>}
                </p>
                <p className="mt-0.5 text-xs text-slate-500">{t.category}</p>
              </div>
            </div>
            {t.message_en && <p className="mt-2 line-clamp-2 text-xs text-slate-600">{t.message_en}</p>}
            {t.message_hi && <p className="mt-1 line-clamp-2 text-xs text-slate-500">{t.message_hi}</p>}
            <div className="mt-4 flex gap-1.5">
              <button onClick={() => setEditing({ ...BLANK, ...t })} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Edit" data-testid={`template-edit-${t.id}`}><Pencil size={13} /></button>
              <button onClick={() => duplicate(t)} className="rounded-md border border-gray-200 p-1.5 text-navy hover:border-saffron-500" aria-label="Duplicate" data-testid={`template-dup-${t.id}`}><Copy size={13} /></button>
              <button onClick={() => remove(t)} className="rounded-md border border-gray-200 p-1.5 text-red-500 hover:border-red-400" aria-label="Delete" data-testid={`template-del-${t.id}`}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
        {items.length === 0 && <p className="col-span-full rounded-lg border border-dashed border-gray-300 py-12 text-center text-sm text-slate-400">No templates yet.</p>}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? `Edit — ${editing.name}` : "New Template"} testid="template-editor" wide>
          <div className="grid gap-5 sm:grid-cols-2">
            <TextF label="Template Name" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} required />
            <SelectF label="Category" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} options={CATS} />
          </div>
          <div className="mt-5 space-y-5">
            <AreaF label="Message (English)" value={editing.message_en} onChange={(v) => setEditing({ ...editing, message_en: v })} rows={3} ai contentType="greeting" />
            <AreaF label="Message (Hindi)" value={editing.message_hi} onChange={(v) => setEditing({ ...editing, message_hi: v })} rows={3} ai contentType="greeting" />
            <div className="grid gap-5 sm:grid-cols-2">
              <TextF label="Quote (optional)" value={editing.quote} onChange={(v) => setEditing({ ...editing, quote: v })} />
              <TextF label="Quote Author" value={editing.quote_author} onChange={(v) => setEditing({ ...editing, quote_author: v })} />
            </div>
            <ImageF label="Background Image (optional)" value={editing.bg_image} onChange={(v) => setEditing({ ...editing, bg_image: v })} />
            <CheckF label="Set as Default template" value={editing.is_default} onChange={(v) => setEditing({ ...editing, is_default: v })} />
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setEditing(null)} className="rounded-md border border-gray-200 px-5 py-2.5 text-sm font-semibold text-navy">Cancel</button>
            <button onClick={save} className="btn-primary !rounded-md !px-6 !py-2.5 text-sm" data-testid="template-save">Save</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
